import {
    MarkerClusterer,
    SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import {
    CATEGORIES,
    CLUSTER_CONFIG,
    MARKER_COLORS,
    MAX_DISPLAY,
} from "./consts.js";
import MarkerComponent from "./markerComponent.js";
import PropertyService from "./propertyService.js";
import buttonsFavorite from "../../../components/buttons/_favorite.js";
import { countUpAction } from "../../../helpers.js";

class MarkerManager {
    constructor(map, stateManager, enableClustering = true) {
        this.map = map;
        this.stateManager = stateManager;

        // カテゴリーごとにマーカーを管理するためのオブジェクト
        this.categoryMarkers = Object.values(CATEGORIES).reduce(
            (acc, category) => {
                acc[category] = [];
                return acc;
            },
            {}
        );

        // クラスタリング機能の有効/無効フラグ
        this.enableClustering = enableClustering;

        // カテゴリーごとのクラスタラーを管理
        this.categoryClusterers = {};
    }

    async #addProperty(properties) {
        this.stateManager.elements.propertyContainer.insertAdjacentHTML(
            "beforeend",
            properties
        );
        buttonsFavorite();
    }

    // 選択されていないマーカーのスタイルを更新
    #updateMarkerStyles(markers) {
        markers.forEach((marker) => {
            const type = marker.querySelector("svg").getAttribute("data-type");
            const textElement = marker.querySelector("svg text");
            const backgroundElement = marker.querySelector("svg .background");

            textElement.style.fill = MARKER_COLORS[type];
            backgroundElement.style.fill = "#fff";
        });
    }

    // 選択されているマーカーのスタイルを更新
    #updateSelectedMarkerStyle(marker, type) {
        const backgroundElement = marker.querySelector("svg .background");
        const textElement = marker.querySelector("svg text");

        backgroundElement.style.fill = MARKER_COLORS[type];
        textElement.style.fill = "#fff";
    }

    // マーカークリック時の処理
    async #handleMarkerClick(marker, property) {
        const markers = document.querySelectorAll("gmp-advanced-marker");
        this.#updateMarkerStyles(markers);

        const type = marker.querySelector("svg").getAttribute("data-type");
        this.#updateSelectedMarkerStyle(marker, type);

        await this.#updatePropertyContainer([property.id]);
        countUpAction(this.stateManager.elements.count, 1);
    }

    // クラスタークリック時の処理
    async #handleClusterClick(cluster) {
        const markers = document.querySelectorAll("gmp-advanced-marker");
        this.#updateMarkerStyles(markers);

        const type = cluster.marker
            .querySelector("svg")
            .getAttribute("data-type");
        this.#updateSelectedMarkerStyle(cluster.marker, type);

        const ids = cluster.markers.map((marker) => marker.propertyId);
        await this.#updatePropertyContainer(ids);

        // 実際に表示される件数（最大20）でカウントアップ
        const displayCount = Math.min(ids.length, MAX_DISPLAY);
        countUpAction(this.stateManager.elements.count, displayCount);
        return false;
    }

    // 物件コンテナを更新
    async #updatePropertyContainer(propertyIds) {
        const limitedIds = propertyIds.slice(0, MAX_DISPLAY);
        const { propertyContainer } = this.stateManager.elements;

        propertyContainer.innerHTML =
            "<strong style='color: #000;'>読み込み中です</strong>";

        try {
            const properties = await PropertyService.fetchPropertiesByIds(
                limitedIds,
                this.stateManager.getTransactionType()
            );
            propertyContainer.innerHTML = "";
            this.#addProperty(properties);
        } catch (error) {
            console.error("物件カードの取得に失敗しました:", error);
            propertyContainer.innerHTML =
                "<strong style='color: #000;'>データの取得に失敗しました</strong>";
        }

        // 検索ボタンのhrefを更新
        const searchButton = this.stateManager.elements.searchButton;
        if (searchButton) {
            const baseUrl = searchButton.getAttribute("href").split("?")[0];
            const params = new URLSearchParams();

            // 制限後のIDを使用
            limitedIds.forEach((id) => params.append("ids[]", id));

            searchButton.setAttribute(
                "href",
                `${baseUrl}?${params.toString()}`
            );
        }
    }

    // マーカーを作成
    async #createMarker(property) {
        const { AdvancedMarkerElement } = await google.maps.importLibrary(
            "marker"
        );
        const markerSvg = MarkerComponent.render(
            property.category_type,
            property.count || 1
        );

        const marker = new AdvancedMarkerElement({
            position: {
                lat: property.geopoint.lat,
                lng: property.geopoint.lng,
            },
            map: this.map,
            content: markerSvg,
        });
        marker.propertyId = property.id;

        marker.addListener("gmp-click", () => {
            window.dispatchEvent(new Event("marker:click"));
            this.#handleMarkerClick(marker, property);
        });

        if (this.categoryMarkers[property.category_type]) {
            this.categoryMarkers[property.category_type].push(marker);
        }

        return marker;
    }

    // マーカーをクリア
    clearMarkers() {
        Object.values(this.categoryMarkers).forEach((markers) => {
            markers.forEach((marker) => marker.setMap(null));
        });

        Object.keys(this.categoryMarkers).forEach((category) => {
            this.categoryMarkers[category] = [];
        });

        // クラスタリングが有効な場合はクラスタラーもクリア
        if (this.enableClustering) {
            Object.values(this.categoryClusterers).forEach((clusterer) => {
                if (clusterer) {
                    clusterer.clearMarkers();
                }
            });
        }
    }

    // マーカーを更新
    async updateMarkers(properties) {
        this.clearMarkers();
        await Promise.all(
            properties.map((property) => this.#createMarker(property))
        );

        // クラスタリングが有効な場合のみクラスタラーを更新
        if (this.enableClustering) {
            this.updateClusterers();
        }
    }

    // クラスタラーを更新
    updateClusterers() {
        Object.keys(this.categoryMarkers).forEach((category) => {
            if (this.categoryClusterers[category]) {
                this.categoryClusterers[category].clearMarkers();
                this.categoryClusterers[category].addMarkers(
                    this.categoryMarkers[category]
                );
            }
        });
    }

    // カテゴリーごとにクラスタラーを初期化
    async initializeClusterers() {
        if (!this.enableClustering) {
            return;
        }

        Object.keys(this.categoryMarkers).forEach((category) => {
            this.categoryClusterers[category] = new MarkerClusterer({
                map: this.map,
                markers: [],
                algorithm: new SuperClusterAlgorithm(CLUSTER_CONFIG),
                renderer: {
                    render: ({ count, position }) => {
                        const markerSvg = MarkerComponent.render(
                            category,
                            count
                        );
                        return new google.maps.marker.AdvancedMarkerElement({
                            position,
                            content: markerSvg,
                        });
                    },
                },
                onClusterClick: (event, cluster, map) => {
                    window.dispatchEvent(new Event("cluster:click"));
                    this.#handleClusterClick(cluster);
                },
            });
        });
    }
}

export default MarkerManager;
